import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { startTransition, useMemo } from 'react';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';
import { useMutation, useReadQuery, useSuspenseQuery } from '@apollo/client/react';

import { Button } from '@/components/ui/button';
import { EscrowMemberRemoveDocument } from 'src/graphql/actions/escrowMembers/escrowMemberRemove.mutation';
import { ProfileDocument } from 'src/graphql/actions/profile/profile.query';
import { withSuspense } from 'src/hoc';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { getGravatarUrl } from 'src/utils/getGravatarUrl';
import { cn } from 'src/lib/utils';
import { UserRoleEnum } from 'src/graphql/schema';

import { getMemberSortPriority } from '../lib';
import { refetchUsers, refetchEscrowMembers } from '../api';
import { useMembersManagement } from '../model/MembersManagementContext';

import RoleCell from './RoleCell';

type IMemberRow = {
  id: string;
  user: {
    id: string;
    name: string;
    email: string;
    role: UserRoleEnum;
  };
  role: string;
  action: {
    canUpdate: boolean;
    canRemove: boolean;
  };
};

const RemoveCell = ({ id, canRemove }: { id: string; canRemove: boolean }) => {
  const onCompleted = () =>
    startTransition(() => {
      refetchUsers();
      refetchEscrowMembers();
    });

  const [removeEscrowMember, { loading }] = useMutation(EscrowMemberRemoveDocument, {
    onCompleted,
  });

  const onRemove = async () => {
    try {
      await removeEscrowMember({
        variables: {
          escrowMemberId: id,
        },
      });
      toast.success('Member access has been removed');
    } catch (error) {
      toast.error('Failed to remove member access');
    }
  };

  if (canRemove) {
    return (
      <Button variant="ghost" onClick={onRemove} disabled={loading} isLoading={loading} size="sm">
        <Trash2 className="text-destructive" />
      </Button>
    );
  }

  return null;
};

const columnHelper = createColumnHelper<IMemberRow>();
const columns = [
  columnHelper.accessor('user', {
    cell: (props) => {
      const { name, email } = props.getValue();
      const fallback = name.charAt(0).toUpperCase();

      return (
        <div className="flex items-center gap-2">
          <Avatar className="size-8 md:size-10 rounded-full">
            <AvatarImage src={getGravatarUrl(email)} />
            <AvatarFallback className="rounded-full bg-primary text-white">{fallback}</AvatarFallback>
          </Avatar>
          <span className="text-sm text-foreground font-medium line-clamp-1">{name}</span>
        </div>
      );
    },
  }),
  columnHelper.accessor('role', {
    cell: (props) => (
      <div className="flex justify-end">
        <RoleCell role={props.getValue()} id={props.row.original.id} canUpdate={props.row.original.action.canUpdate} />
      </div>
    ),
  }),
  columnHelper.accessor('action', {
    cell: (props) => <RemoveCell id={props.row.original.id} canRemove={props.row.original.action.canRemove} />,
  }),
];

const AddedMembers = () => {
  const { queryEscrowMembersRef } = useMembersManagement();
  const {
    data: { escrowMembers },
  } = useReadQuery(queryEscrowMembersRef);
  const {
    data: { profile },
  } = useSuspenseQuery(ProfileDocument);

  const membersMapped = useMemo(() => {
    const sortedNodes = escrowMembers.nodes.slice().sort((a, b) => {
      const priorityA = getMemberSortPriority(a.user, profile.id);
      const priorityB = getMemberSortPriority(b.user, profile.id);

      if (priorityA !== priorityB) return priorityA - priorityB;

      return 0;
    });

    return sortedNodes.map((member) => ({
      id: member.id,
      user: {
        id: member.user.id,
        name: member.user.name || '-',
        email: member.user.email || '-',
        role: member.user.role,
      },
      role: member.role,
      action: {
        canUpdate: member.policy.update,
        canRemove: member.policy.remove,
      },
    }));
  }, [escrowMembers.nodes, profile.id]);
  const table = useReactTable({
    data: membersMapped,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="space-y-4 overflow-hidden max-h-[400px] md:max-h-none flex flex-col px-6">
      <h2 className="text-base text-foreground">Escrow members ({escrowMembers.nodesCount})</h2>
      <div className="overflow-hidden rounded-sm border border-border grow">
        <Table maxHeight="h-full">
          <TableHeader className="sticky top-0 bg-white z-10 after:absolute after:left-0 after:bottom-0 after:w-full after:border-b after:border-border">
            <TableRow>
              <TableHead>Team member</TableHead>
              <TableHead className="text-right">Permissions</TableHead>
              <TableHead className="w-[72px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                className={cn(
                  shouldHighlightMemberRow(row.original.user, profile.id) && 'bg-slate-100 hover:bg-slate-100',
                )}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default withSuspense(AddedMembers);

function shouldHighlightMemberRow({ id, role }: { id: string; role: UserRoleEnum }, currentUserId: string) {
  return id === currentUserId || role === UserRoleEnum.Owner;
}
